import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';

const fmtA = (v) => (v == null ? '—' : `${v < 0 ? '−' : ''}A$${Math.round(Math.abs(v)).toLocaleString()}`);
const W = 1200; const H = 230; const PL = 60; const PR = 16; const PT = 24; const PB = 34;
const STEPS = [['uplift_aud', 'Uplift', '#047857'], ['discount_aud', 'Discount change', '#B45309'], ['expiring_aud', 'Terms ending', '#BE123C']];
const DEAL_COLORS = ['#0F2040', '#4ABFCF', '#F59E0B', '#8B5CF6', '#10B981', '#E11D48', '#6366F1', '#F97316', '#0EA5E9', '#A3E635', '#EC4899', '#78716C'];

/** Splits a bar's deal parts into stacked segments: positives stack up from `base`, negatives stack down. */
const segments = (parts, base) => {
  let up = base; let down = base; const out = [];
  parts.forEach((p) => {
    if (!p.value) return;
    if (p.value > 0) { out.push({ ...p, from: up, to: up + p.value }); up += p.value; } else { out.push({ ...p, from: down + p.value, to: down }); down += p.value; }
  });
  return out;
};

/** ARR waterfall: Year n ARR → uplift → discount change → terms ending → Year n+1 ARR, for every transition in the term. Bars stack per deal when more than one deal is in view. */
export function ArrWaterfall({ bridge, onPickDeal, onFilterDeal }) {
  const [hover, setHover] = useState(null);
  const deals = useMemo(() => {
    const seen = new Map();
    (bridge || []).forEach((b) => (b.deals || []).forEach((d) => { if (!seen.has(d.id)) seen.set(d.id, { ...d, color: DEAL_COLORS[seen.size % DEAL_COLORS.length] }); }));
    return [...seen.values()];
  }, [bridge]);
  if (!bridge?.length) return null;
  const stacked = deals.length > 1;
  const colorOf = (id) => deals.find((d) => d.id === id)?.color || '#94A3B8';
  const partsFor = (b, key) => (b.deals || []).map((d) => ({ id: d.id, name: d.name, org: d.organization_name, value: d[key] || 0 }));
  const bars = [];
  bridge.forEach((b, i) => {
    if (i === 0) bars.push({ label: `Y${b.from_year} ARR`, kind: 'total', from: 0, to: b.start_aud, value: b.start_aud, parts: partsFor(b, 'start_aud') });
    let run = b.start_aud;
    STEPS.forEach(([k, label, color]) => { bars.push({ label, sub: `Y${b.from_year}→${b.to_year}`, kind: 'step', from: run, to: run + b[k], value: b[k], color, parts: partsFor(b, k) }); run += b[k]; });
    bars.push({ label: `Y${b.to_year} ARR`, kind: 'total', from: 0, to: b.end_aud, value: b.end_aud, detail: `${b.continuing} continuing · ${b.ending} ending`, parts: partsFor(b, 'end_aud') });
  });
  const max = Math.max(1, ...bars.map((x) => Math.max(x.from, x.to)));
  const y = (v) => PT + ((max - v) * (H - PT - PB)) / max;
  const bw = (W - PL - PR) / bars.length; const inner = bw * 0.6;
  const dim = (id) => (hover && hover !== id ? 0.25 : 1);
  return (
    <Card className="border-slate-200" data-testid="arr-waterfall">
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">ARR waterfall</p>
        <p className="text-[11px] text-slate-500 mb-2">How subscription ARR moves year to year: price uplift on continuing deals, change in term + discretionary discounts, and deals whose term ends.{stacked ? ' Each bar is stacked by deal — hover a legend chip to highlight a deal, click it to filter the page to that deal, or click a segment to open that deal\'s year-by-year grid.' : ''} Quantities are fixed per deal, so expansions appear only when a deal's subscription is re-saved with more users.</p>
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="ARR waterfall">
          {[0, max / 2, max].map((v) => <g key={v}><line x1={PL} x2={W - PR} y1={y(v)} y2={y(v)} stroke="#E2E8F0" strokeWidth="0.8" /><text x={PL - 6} y={y(v) + 4} textAnchor="end" fontSize="10" fill="#64748B">{`A$${Math.round(v / 1000).toLocaleString()}k`}</text></g>)}
          {bars.map((b, i) => {
            const x = PL + i * bw + (bw - inner) / 2;
            const top = Math.min(b.from, b.to); const bottom = Math.max(b.from, b.to);
            const h = Math.max(1.5, y(top) - y(bottom));
            const fill = b.kind === 'total' ? '#0F2040' : b.color;
            const next = bars[i + 1];
            const title = `${b.label}${b.sub ? ` (${b.sub})` : ''}: ${fmtA(b.value)}${b.detail ? ` · ${b.detail}` : ''}`;
            const segs = stacked ? segments(b.parts, b.kind === 'total' ? 0 : b.from) : [];
            return (
              <g key={i} data-testid={`arr-bar-${i}`}>
                {stacked && segs.length ? segs.map((s) => (
                  <rect key={s.id} x={x} y={y(s.to)} width={inner} height={Math.max(1, y(s.from) - y(s.to))} fill={colorOf(s.id)} opacity={dim(s.id)} stroke="#fff" strokeWidth="0.6" style={{ cursor: onPickDeal ? 'pointer' : 'default' }}
                    onMouseEnter={() => setHover(s.id)} onMouseLeave={() => setHover(null)} onClick={() => onPickDeal?.(s.id)} data-testid={`arr-bar-${i}-deal-${s.id}`}>
                    <title>{`${s.name} (${s.org}) — ${b.label}${b.sub ? ` ${b.sub}` : ''}: ${s.value > 0 && b.kind === 'step' ? '+' : ''}${fmtA(s.value)}\nTotal ${title}${onPickDeal ? '\nClick to open the deal\'s year-by-year grid' : ''}`}</title>
                  </rect>
                )) : (
                  <rect x={x} y={y(bottom)} width={inner} height={h} fill={fill} rx="2" opacity={b.kind === 'step' && !b.value ? 0.25 : 1}><title>{title}</title></rect>
                )}
                {next && <line x1={x + inner} x2={x + bw} y1={y(b.to)} y2={y(b.to)} stroke="#94A3B8" strokeDasharray="3 2" strokeWidth="0.8" />}
                <text x={x + inner / 2} y={y(bottom) - 4} textAnchor="middle" fontSize="10" fill={b.kind === 'total' ? '#0F2040' : b.value < 0 ? '#BE123C' : '#047857'} fontWeight="600">{b.kind === 'total' ? fmtA(b.value) : b.value ? `${b.value > 0 ? '+' : ''}${fmtA(b.value)}` : ''}</text>
                <text x={x + inner / 2} y={H - 18} textAnchor="middle" fontSize="10" fill="#334155">{b.label}</text>
                {b.sub && <text x={x + inner / 2} y={H - 6} textAnchor="middle" fontSize="9" fill="#94A3B8">{b.sub}</text>}
              </g>
            );
          })}
        </svg>
        {stacked && (
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2" data-testid="arr-waterfall-legend">
            {deals.map((d) => (
              <button key={d.id} type="button" onMouseEnter={() => setHover(d.id)} onMouseLeave={() => setHover(null)} onClick={() => { setHover(null); onFilterDeal?.(d); }}
                className={`flex items-center gap-1.5 text-[10px] text-slate-600 rounded px-1 transition-opacity hover:bg-slate-100 ${hover && hover !== d.id ? 'opacity-40' : ''}`}
                title={`${d.name} · ${d.organization_name} · Y1 ARR ${fmtA(d.start_aud)}${onFilterDeal ? '\nClick to show only this deal' : ''}`} data-testid={`arr-legend-${d.id}`}>
                <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: d.color }} />
                <span className="truncate max-w-[200px]">{d.name}</span>
                <span className="text-slate-400">· {d.organization_name}</span>
              </button>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
